import React, { PropsWithChildren } from 'react';
import { QueryClient, QueryClientProvider, setLogger } from 'react-query';
import { persistQueryClient, PersistedClient } from 'react-query/persistQueryClient-experimental';
import { createWebStoragePersistor } from 'react-query/createWebStoragePersistor-experimental';
import { ReactQueryDevtools } from 'react-query/devtools';
import { AxiosError } from 'axios';
import AES from 'crypto-js/aes';
import enc from 'crypto-js/enc-utf8';
import { redirectToSecureFrontend } from './auth';

export const persistorKey = 'REACT_QUERY_OFFLINE_CACHE';
export const persistorStorage: 'localStorage' | 'sessionStorage' = 'sessionStorage';
export const persistorPassphrase = process.env.REACT_APP_REACT_QUERY_OFFLINE_CACHE_PASSPHRASE;

const persistor = createWebStoragePersistor({
    key: persistorKey,
    storage: window[persistorStorage],
    serialize: (client: PersistedClient): string => {
        if (!persistorPassphrase) {
            return JSON.stringify(client);
        }
        return AES.encrypt(JSON.stringify(client), persistorPassphrase).toString();
    },
    deserialize: (cachedString: string): PersistedClient => {
        if (!persistorPassphrase) {
            return JSON.parse(cachedString);
        }
        const bytes = AES.decrypt(cachedString, persistorPassphrase);
        return JSON.parse(bytes.toString(enc));
    },
});

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            refetchOnWindowFocus: false,
            onError: (error) => {
                const axiosErr = error as AxiosError;

                if (
                    (axiosErr.response?.status === 401 &&
                        axiosErr.response?.config.url !== '/oauth2-client/auth/check-token') ||
                    (typeof axiosErr.response === 'undefined' &&
                        (axiosErr.config?.baseURL ?? '').includes('/api/'))
                ) {
                    redirectToSecureFrontend();
                }

                return error;
            },
        },
    },
});

persistQueryClient({
    queryClient,
    persistor,
});

export const ReactQueryProvider = ({ children }: PropsWithChildren<unknown>) => {
    const isDebugging = process.env.REACT_APP_HTTP_CLIENT_DEBUGGING === 'true' ?? false;

    setLogger(
        process.env.NODE_ENV === 'production' || !isDebugging
            ? {
                  log: () => {},
                  warn: () => {},
                  error: () => {},
              }
            : window.console
    );

    return (
        <QueryClientProvider client={queryClient}>
            {children}
            {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
        </QueryClientProvider>
    );
};
